import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import {
    createAwarenessCampaign,
    updateAwarenessCampaign,
} from "../vsecuriteSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * AwarenessForm — Create or edit an awareness campaign.
 *
 * Props:
 *   campaign (Object|null) — null → create mode; object → edit mode
 *   onSave(savedCampaign)  — called after a successful save
 *   onCancel()             — called when the user cancels
 *   teams (Array)          — array of { id, name } available for targeting
 *   open (boolean)         — controls dialog visibility on mobile
 */
const AwarenessForm = ({ campaign, onSave, onCancel, teams = [], open = true }) => {
    const dispatch = useDispatch();

    /** Detect whether we are in edit or create mode */
    const isEditMode = campaign != null;

    /** Redux loading flag */
    const loading = useSelector((state) => state.vsecurite.awareness.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mobile = isMobile === 2;

    /* ---------------------------------------------------------------------- */
    /* Form field state                                                        */
    /* ---------------------------------------------------------------------- */

    const [titre, setTitre] = useState("");
    const [description, setDescription] = useState("");
    const [contenu, setContenu] = useState("");
    const [targetType, setTargetType] = useState("all"); // 'all' | 'teams'
    const [selectedTeams, setSelectedTeams] = useState([]);

    const [teamSearch, setTeamSearch] = useState("");

    /* Validation error messages keyed by field name */
    const [errors, setErrors] = useState({});

    /* Local submitting flag to prevent double-submit */
    const [submitting, setSubmitting] = useState(false);

    /**
     * Pre-populate fields when editing an existing campaign.
     * Resets back to defaults when the form is used in create mode.
     */
    useEffect(() => {
        if (campaign) {
            setTitre(campaign.title || campaign.titre || "");
            setDescription(campaign.description || "");
            setContenu(campaign.content || campaign.contenu || "");
            setTargetType(campaign.target_type || "all");
            setSelectedTeams(
                Array.isArray(campaign.team_ids) ? campaign.team_ids.map(Number) : []
            );
        } else {
            setTitre("");
            setDescription("");
            setContenu("");
            setTargetType("all");
            setSelectedTeams([]);
        }
        setErrors({});
    }, [campaign]);

    /**
     * Validates all required form fields.
     * Populates the `errors` state object and returns a boolean.
     * @returns {boolean} true when the form is valid
     */
    const validate = () => {
        const newErrors = {};

        if (!titre.trim()) {
            newErrors.titre = Localize("VSEC_ERROR_TITLE_REQUIRED", lang);
        }

        if (!contenu.trim()) {
            newErrors.contenu = Localize("VSEC_ERROR_CONTENT_REQUIRED", lang);
        }

        if (targetType === "teams" && selectedTeams.length === 0) {
            newErrors.teams = Localize("VSEC_ERROR_TEAM_REQUIRED", lang);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handles the "Enregistrer" submit action.
     * Validates, then dispatches create or update thunk depending on mode.
     * Shows toast feedback and calls onSave() on success.
     */
    const handleSubmit = async () => {
        if (!validate()) return;
        if (submitting) return;

        setSubmitting(true);

        const payload = {
            title: titre.trim(),
            description: description.trim(),
            content: contenu.trim(),
            target_type: targetType,
            team_ids: targetType === "teams" ? selectedTeams : [],
        };

        try {
            let result;
            if (isEditMode) {
                result = await dispatch(
                    updateAwarenessCampaign({ id: campaign.id, ...payload })
                ).unwrap();
                showSuccessToast(Localize("VSEC_TOAST_CAMPAIGN_UPDATED", lang));
            } else {
                result = await dispatch(createAwarenessCampaign(payload)).unwrap();
                showSuccessToast(Localize("VSEC_TOAST_CAMPAIGN_CREATED", lang));
            }
            onSave && onSave(result);
        } catch {
            showErrorToast(Localize("VSEC_TOAST_GENERIC_ERROR", lang));
        } finally {
            setSubmitting(false);
        }
    };


    const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

    /* Shared sx helpers */
    const inputSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            fontSize: "0.9rem",
            background: "rgba(0,40,128,0.02)",
            transition: `all 250ms ${EASE}`,
            "&.Mui-focused": {
                background: "rgba(255,255,255,0.95)",
                boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF9015",
            },
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: "#FF9015",
        },
        "& input": { fontSize: "16px" },
        "& textarea": { fontSize: "16px" },
    };

    const isLoading = loading || submitting;

    /* ── Form content (shared between mobile and desktop) ── */
    const formContent = (
        <>
            <Box sx={{ p: mobile ? 2 : 3, flex: 1, overflowY: "auto" }}>
                {/* ---------------------------------------------------------------- */}
                {/* Form title (desktop only — mobile shows it in AppBar)            */}
                {/* ---------------------------------------------------------------- */}
                {!mobile && (
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: "rgba(0,40,128,0.85)",
                            mb: 3,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {isEditMode ? <Langs str="VSEC_FORM_EDIT_CAMPAIGN_TITLE" /> : <Langs str="VSEC_FORM_NEW_CAMPAIGN_TITLE" />}
                    </Typography>
                )}

                <Stack spacing={2.5}>
                    {/* ---------------------------------------------------------- */}
                    {/* Titre                                                       */}
                    {/* ---------------------------------------------------------- */}
                    <TextField
                        label={Localize("VSEC_FIELD_TITLE", lang)}
                        value={titre}
                        onChange={(e) => {
                            setTitre(e.target.value);
                            if (errors.titre) setErrors((prev) => ({ ...prev, titre: undefined }));
                        }}
                        error={!!errors.titre}
                        helperText={errors.titre}
                        fullWidth
                        disabled={isLoading}
                        sx={inputSx}
                        inputProps={{ maxLength: 200 }}
                    />

                    {/* ---------------------------------------------------------- */}
                    {/* Description                                                 */}
                    {/* ---------------------------------------------------------- */}
                    <TextField
                        label={Localize("VSEC_FIELD_DESCRIPTION", lang)}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={isLoading}
                        sx={inputSx}
                    />

                    {/* ---------------------------------------------------------- */}
                    {/* Contenu du document                                         */}
                    {/* ---------------------------------------------------------- */}
                    <TextField
                        label={Localize("VSEC_FIELD_CONTENT", lang)}
                        value={contenu}
                        onChange={(e) => {
                            setContenu(e.target.value);
                            if (errors.contenu) setErrors((prev) => ({ ...prev, contenu: undefined }));
                        }}
                        error={!!errors.contenu}
                        helperText={errors.contenu || Localize("VSEC_FIELD_CONTENT_HELPER", lang)}
                        fullWidth
                        multiline
                        rows={mobile ? 5 : 8}
                        disabled={isLoading}
                        sx={inputSx}
                    />

                    <Divider sx={{ borderColor: "rgba(0,40,128,0.06)" }} />

                    {/* ---------------------------------------------------------- */}
                    {/* Type de ciblage                                             */}
                    {/* ---------------------------------------------------------- */}
                    <FormControl component="fieldset" disabled={isLoading}>
                        <FormLabel
                            component="legend"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "rgba(0,40,128,0.7)",
                                mb: 0.5,
                                "&.Mui-focused": { color: "#FF9015" },
                            }}
                        >
                            <Langs str="VSEC_FIELD_TARGET_TYPE" />
                        </FormLabel>
                        <RadioGroup
                            row={!mobile}
                            value={targetType}
                            onChange={(e) => {
                                setTargetType(e.target.value);
                                /* Reset team error when switching back to 'all' */
                                if (e.target.value === "all") {
                                    setErrors((prev) => ({ ...prev, teams: undefined }));
                                }
                            }}
                        >
                            <FormControlLabel
                                value="all"
                                control={
                                    <Radio
                                        sx={{
                                            color: "rgba(0,40,128,0.3)",
                                            "&.Mui-checked": { color: "#FF9015" },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "0.875rem" }}>
                                        <Langs str="VSEC_TARGET_ALL_COMPANY" />
                                    </Typography>
                                }
                                sx={{ minHeight: 44 }}
                            />
                            <FormControlLabel
                                value="teams"
                                control={
                                    <Radio
                                        sx={{
                                            color: "rgba(0,40,128,0.3)",
                                            "&.Mui-checked": { color: "#FF9015" },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "0.875rem" }}>
                                        <Langs str="VSEC_TARGET_SPECIFIC_TEAMS" />
                                    </Typography>
                                }
                                sx={{ minHeight: 44 }}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* ---------------------------------------------------------- */}
                    {/* Team checkboxes — shown only when targetType === 'teams'   */}
                    {/* ---------------------------------------------------------- */}
                    {targetType === "teams" && (
                        <FormControl fullWidth error={!!errors.teams} disabled={isLoading}>
                            <InputLabel id="awareness-teams-label">
                                <Langs str="VSEC_FIELD_TEAMS_LABEL" />
                            </InputLabel>
                            <Select
                                labelId="awareness-teams-label"
                                multiple
                                value={selectedTeams}
                                label={<Langs str="VSEC_FIELD_TEAMS_LABEL" />}
                                onChange={(e) => {
                                    setSelectedTeams(e.target.value);
                                    if (errors.teams) setErrors((prev) => ({ ...prev, teams: undefined }));
                                }}
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {selected.map((id) => {
                                            const team = teams.find((t) => t.id === id);
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={team ? (team.label || team.name || team.nom) : id}
                                                    size="small"
                                                    sx={{
                                                        background: "rgba(255,144,21,0.12)",
                                                        color: "#FF9015",
                                                        fontWeight: 600,
                                                        fontSize: "0.78rem",
                                                        borderRadius: "6px",
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                                sx={{
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF9015" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FF9015" },
                                    "& .MuiSelect-select": { fontSize: "16px" },
                                }}
                                MenuProps={{
                                    PaperProps: { style: { maxHeight: 320 } },
                                    autoFocus: false,
                                    sx: { zIndex: 1500 },
                                }}
                                onClose={() => setTeamSearch("")}
                            >
                                <Box sx={{ px: 1.5, pt: 1, pb: 0.5, position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder={Localize("VSEC_SEARCH_TEAMS", lang)}
                                        value={teamSearch}
                                        onChange={(e) => setTeamSearch(e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <span style={{ fontSize: "1rem" }}>🔍</span>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF9015" },
                                            "& input": { fontSize: "16px" },
                                        }}
                                    />
                                </Box>
                                {teams
                                    .filter((t) => {
                                        const name = (t.label || t.name || t.nom || "").toLowerCase();
                                        return name.includes(teamSearch.toLowerCase());
                                    })
                                    .map((team) => (
                                        <MenuItem key={team.id} value={team.id} sx={{ minHeight: 44 }}>
                                            {team.label || team.name || team.nom}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            {errors.teams && (
                                <FormHelperText>{errors.teams}</FormHelperText>
                            )}
                        </FormControl>
                    )}

                </Stack>
            </Box>

            {/* ---------------------------------------------------------------- */}
            {/* Action buttons — sticky at the bottom                           */}
            {/* ---------------------------------------------------------------- */}
            <Box
                sx={{
                    px: mobile ? 2 : 3,
                    py: 2,
                    borderTop: "1px solid rgba(0,40,128,0.06)",
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    borderRadius: mobile ? 0 : "0 0 20px 20px",
                    flexShrink: 0,
                    position: "sticky",
                    bottom: 0,
                }}
            >
                <Stack
                    direction={mobile ? "column-reverse" : "row"}
                    spacing={1.5}
                    justifyContent="flex-end"
                >
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={isLoading}
                        fullWidth={mobile}
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            px: 3,
                            minHeight: 48,
                            borderColor: "rgba(0,40,128,0.15)",
                            color: "rgba(0,40,128,0.7)",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                borderColor: "rgba(0,40,128,0.35)",
                                bgcolor: "rgba(0,40,128,0.04)",
                                transform: "scale(1.02)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        fullWidth={mobile}
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : null
                        }
                        sx={{
                            bgcolor: "#FF9015",
                            color: "#fff",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            px: 3,
                            minHeight: 48,
                            boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                bgcolor: "#e07d0a",
                                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
                                transform: "translateY(-1px) scale(1.02)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                            "&.Mui-disabled": {
                                bgcolor: "rgba(255,144,21,0.45)",
                                color: "#fff",
                            },
                        }}
                    >
                        {isLoading ? <Langs str="VSEC_BTN_SAVING" /> : <Langs str="VSEC_BTN_SAVE" />}
                    </Button>
                </Stack>
            </Box>
        </>
    );

    /* ── Mobile: render in a fullScreen Dialog ── */
    if (mobile) {
        return (
            <Dialog
                open={open}
                onClose={onCancel}
                fullScreen
                TransitionComponent={SlideUp}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        bgcolor: "#fff",
                        color: "rgba(30,30,30,0.9)",
                    },
                }}
            >
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: "#fff",
                        color: "rgba(30,30,30,0.9)",
                        borderBottom: "1px solid rgba(0,40,128,0.06)",
                    }}
                >
                    <Toolbar>
                        <IconButton edge="start" onClick={onCancel} sx={{ color: "rgba(0,40,128,0.7)" }}>
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 1, fontWeight: 700, fontSize: "1rem", flex: 1, color: "rgba(0,40,128,0.85)" }}>
                            {isEditMode ? <Langs str="VSEC_FORM_EDIT_CAMPAIGN_TITLE" /> : <Langs str="VSEC_FORM_NEW_CAMPAIGN_TITLE" />}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                    {formContent}
                </Box>
            </Dialog>
        );
    }

    /* ── Desktop: inline card ── */
    return (
        <Box
            sx={{
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderRadius: "20px",
                border: "1px solid rgba(0,40,128,0.06)",
                boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.05)",
                maxWidth: 720,
                width: "100%",
                mx: "auto",
                maxHeight: "calc(100vh - 160px)",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                "@keyframes formFadeIn": {
                    from: { opacity: 0, transform: "translateY(12px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `formFadeIn 400ms ${EASE} both`,
            }}
        >
            {formContent}
        </Box>
    );
};

export default AwarenessForm;
